import styled from 'styled-components';
import { Palette } from 'config';

/*
  253px = header + bottom margin
  Value cannot be set dynamically to keep
  the layout of other related pages
*/
export const Wrap = styled.div`
  display: flex;
  align-items: stretch;
  min-height: calc(100vh - 253px);
  background-color: ${Palette.White._000};
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 1px 3px 0 rgba(30, 50, 70, 0.1), 0 1px 2px 0 rgba(30, 50, 70, 0.06);
`;

/* ---------- Left column ---------- */

export const Left = styled.div`
  display: flex;
  flex: 1 0 0;
  flex-direction: column;
  align-items: stretch;
  min-width: 0;
`;

export const BannerWrap = styled.div`
  display: flex;
  flex-direction: column;
  padding: 16px;
`;

export const Banner = styled.div`
  position: relative;
  display: flex;
  gap: 24px;
  align-items: flex-start;
  padding: 32px;
  background-color: ${Palette.Primary._000};
  border: 1px solid ${Palette.Primary._200};
  border-radius: 4px;

  /* top accent bar */
  &::after {
    content: '';
    position: absolute;
    inset: 0;
    border-radius: inherit;
    box-shadow: inset 0 4px 0 0 ${Palette.Primary._500};
    pointer-events: none;
  }
`;

export const BannerContent = styled.div`
  display: flex;
  flex: 1 0 0;
  flex-direction: column;
  gap: 16px;
  justify-content: center;
  min-width: 0;
`;

export const UserBlock = styled.div`
  display: flex;
  flex-direction: column;
`;

export const UserName = styled.span`
  font-weight: 600;
  font-size: 24px;
  line-height: 32px;
  color: ${Palette.PrimaryGray._900};
`;

export const UserEmail = styled.span`
  font-size: 12px;
  line-height: 16px;
  color: ${Palette.PrimaryGray._500};
`;

export const Description = styled.p`
  margin: 0;
  font-size: 16px;
  line-height: 24px;
  color: ${Palette.PrimaryGray._700};
`;

export const PrimaryButton = styled.button`
  align-self: flex-start;
  padding: 8px 24px;
  border: none;
  border-radius: 4px;
  background-color: ${Palette.Primary._500};
  box-shadow: 0 2px 2px 0 rgba(28, 53, 106, 0.05);
  font-weight: 500;
  font-size: 14px;
  line-height: 20px;
  color: ${Palette.White._000};
  cursor: pointer;

  &:hover {
    background-color: ${Palette.Primary._600};
  }
`;

export const BannerBadge = styled.div`
  position: relative;
  flex-shrink: 0;
  width: 80px;
  height: 80px;
`;

export const BadgeLogo = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 80px;
  height: 80px;

  svg {
    width: 100%;
    height: 100%;
  }
`;

export const Avatar = styled.div`
  position: absolute;
  top: 20px;
  left: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  border: 2px solid ${Palette.White._000};
  border-radius: 4px;
  background-color: ${Palette.Primary._500};
  font-weight: 500;
  font-size: 12px;
  line-height: 16px;
  color: ${Palette.White._000};
`;

/* ---------- Getting started ---------- */

export const GettingStarted = styled.div`
  display: flex;
  flex: 1 0 0;
  flex-direction: column;
  gap: 16px;
  padding: 24px 32px;
`;

export const SectionHeading = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

export const SectionTitle = styled.span`
  font-weight: 500;
  font-size: 18px;
  line-height: 24px;
  color: ${Palette.PrimaryGray._900};
`;

export const SectionSubtitle = styled.span`
  font-size: 12px;
  line-height: 16px;
  color: ${Palette.PrimaryGray._500};
`;

export const LinkList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  align-items: flex-start;
`;

export const LinkItem = styled.a`
  display: flex;
  gap: 8px;
  align-items: center;
  font-weight: 500;
  font-size: 14px;
  line-height: 20px;
  color: ${Palette.Primary._500};
  text-decoration: none;
  cursor: pointer;

  svg {
    flex-shrink: 0;
  }

  &:hover {
    text-decoration: underline;
  }
`;

/* ---------- Divider ---------- */

export const Divider = styled.div`
  align-self: stretch;
  flex-shrink: 0;
  width: 1px;
  background-color: ${Palette.PrimaryGray._200};
`;

/* ---------- Right column ---------- */

export const Right = styled.div`
  display: flex;
  flex-direction: column;
  gap: 32px;
  flex-shrink: 0;
  width: 464px;
  padding: 24px 32px;
  background-color: ${Palette.PrimaryGray._000};
`;

export const StatusText = styled.div`
  font-size: 14px;
  line-height: 20px;
  color: ${Palette.PrimaryGray._500};
`;

export const ProgressSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

export const ProgressTitle = styled.p`
  margin: 0;
  font-size: 18px;
  line-height: 24px;
  color: ${Palette.PrimaryGray._900};

  strong {
    font-weight: 500;
  }

  span {
    font-weight: 400;
  }
`;

export const ProgressBar = styled.div`
  display: flex;
  gap: 2px;
  overflow: hidden;
  border-radius: 80px;
`;

export const ProgressSegment = styled.div`
  flex: 1 0 0;
  min-width: 0;
  height: 8px;
  background-color: ${({ $filled }) => ($filled ? Palette.Primary._300 : Palette.PrimaryGray._200)};
`;

/* ---------- Setup steps ---------- */

export const StepList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

export const Step = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 16px;
  border-radius: 4px;
  border: 1px solid
    ${({ $state }) =>
      $state === 'active'
        ? Palette.Primary._300
        : $state === 'done'
        ? Palette.PrimaryGray._100
        : Palette.PrimaryGray._200};
  background-color: ${({ $state }) =>
    $state === 'done' ? Palette.PrimaryGray._100 : Palette.White._000};
  ${({ $clickable }) => $clickable && 'cursor: pointer;'}

  &:hover {
    ${({ $clickable }) => $clickable && 'border-color: ' + Palette.Primary._300 + ';'}
  }
`;

export const StepRow = styled.div`
  display: flex;
  gap: 16px;
  align-items: ${({ $align }) => $align || 'center'};
`;

export const StepDoneBadge = styled.div`
  display: flex;
  flex-shrink: 0;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  border-radius: 80px;
  background-color: ${Palette.Teal._500};

  svg {
    width: 14px;
    height: auto;
  }
`;

export const StepNumber = styled.div`
  display: flex;
  flex-shrink: 0;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  border-radius: 80px;
  font-weight: 500;
  font-size: 12px;
  line-height: 16px;
  ${({ $state }) =>
    $state === 'active'
      ? `
        background-color: ${Palette.Primary._500};
        border: 2px solid ${Palette.Primary._500};
        color: ${Palette.White._000};
      `
      : `
        background-color: ${Palette.PrimaryGray._100};
        border: 1px solid ${Palette.PrimaryGray._300};
        color: ${Palette.PrimaryGray._500};
      `}
`;

export const StepContent = styled.div`
  display: flex;
  flex: 1 0 0;
  flex-direction: column;
  gap: 8px;
  justify-content: center;
  min-width: 0;
`;

export const StepLabel = styled.span`
  font-weight: 500;
  font-size: 14px;
  line-height: 20px;
  color: ${({ $muted }) => ($muted ? Palette.PrimaryGray._500 : Palette.PrimaryGray._900)};
`;

export const StepDescription = styled.span`
  font-size: 12px;
  line-height: 16px;
  color: ${Palette.PrimaryGray._700};
`;

export const StepButton = styled.button`
  width: 100%;
  padding: 4px 16px;
  border: none;
  border-radius: 4px;
  background-color: ${Palette.Primary._500};
  box-shadow: 0 2px 2px 0 rgba(28, 53, 106, 0.05);
  font-weight: 500;
  font-size: 14px;
  line-height: 20px;
  color: ${Palette.White._000};
  cursor: pointer;

  &:hover {
    background-color: ${Palette.Primary._600};
  }
`;
